/**
 * AgentBuilderPage — Create/edit agent configurations with template support.
 * Task 4.10
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bot, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { TemplateGrid } from "@/components/agents/TemplateGrid";
import { useAgentStore } from "@/stores/useAgentStore";
import type { AgentTemplate } from "@/types/agent";

const modelOptions = [
  "claude-sonnet-4-20250514",
  "claude-opus-4-20250514",
  "gpt-4o",
  "gpt-4o-mini",
  "gemini-2.0-flash",
];

const defaultPermissions = [
  "file_read",
  "file_write",
  "bash",
  "web_search",
  "edit",
  "glob",
  "grep",
];

type Step = "choose" | "configure" | "review";

export function AgentBuilderPage() {
  const navigate = useNavigate();
  const { templates, loadTemplates, createAgent, isLoading } = useAgentStore();

  const [step, setStep] = useState<Step>("choose");
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [model, setModel] = useState(modelOptions[0]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [permissions, setPermissions] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleTemplateSelect = useCallback((tpl: AgentTemplate) => {
    setSelectedTemplate(tpl);
    // Pre-fill from template
    if (tpl.defaultConfig?.name) setName(tpl.defaultConfig.name);
    if (tpl.defaultConfig?.model) setModel(tpl.defaultConfig.model);
    if (tpl.defaultConfig?.systemPrompt) setSystemPrompt(tpl.defaultConfig.systemPrompt);
    if (tpl.defaultConfig?.permissions) setPermissions(tpl.defaultConfig.permissions);
    setStep("configure");
  }, []);

  const handleStartScratch = useCallback(() => {
    setSelectedTemplate(null);
    setName("");
    setModel(modelOptions[0]);
    setSystemPrompt("");
    setPermissions({});
    setStep("configure");
  }, []);

  const togglePermission = useCallback((perm: string) => {
    setPermissions((prev) => {
      const next = { ...prev };
      if (next[perm] === "allow") {
        delete next[perm];
      } else {
        next[perm] = "allow";
      }
      return next;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Agent name is required";
    if (!model) errs.model = "Model selection is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [name, model]);

  const handleCreate = useCallback(async () => {
    if (!validate()) return;
    const agent = await createAgent({
      name: name.trim(),
      model,
      systemPrompt,
      permissions,
    });
    if (agent) {
      navigate(`/dashboard/agents/${agent.id}`);
    }
  }, [name, model, systemPrompt, permissions, validate, createAgent, navigate]);

  return (
    <div className="flex h-full flex-col" data-testid="agent-builder-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Agent Builder</h1>
            <p className="text-sm text-muted-foreground">
              Create a new AI agent configuration
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="mt-4 flex items-center gap-2">
          {(["choose", "configure", "review"] as const).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              {i > 0 && <div className="h-px w-8 bg-white/10" />}
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
                  step === s
                    ? "bg-primary text-white"
                    : (["choose", "configure", "review"].indexOf(step) >
                      ["choose", "configure", "review"].indexOf(s))
                      ? "bg-primary/20 text-primary"
                      : "bg-white/10 text-muted"
                }`}
              >
                {i + 1}
              </div>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {s === "choose" ? "Start" : s === "configure" ? "Configure" : "Review"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Step 1: Choose */}
        {step === "choose" && (
          <div data-testid="step-choose">
            <h2 className="mb-4 text-lg font-semibold text-white">
              Choose a Starting Point
            </h2>
            <div className="mb-6">
              <Button variant="secondary" onClick={handleStartScratch}>
                <Sparkles className="h-4 w-4" />
                Start from Scratch
              </Button>
            </div>
            {templates.length > 0 && (
              <>
                <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                  Or choose a template:
                </h3>
                <TemplateGrid
                  templates={templates}
                  selectedId={selectedTemplate?.id ?? null}
                  onSelect={handleTemplateSelect}
                />
              </>
            )}
          </div>
        )}

        {/* Step 2: Configure */}
        {step === "configure" && (
          <div className="max-w-2xl space-y-6" data-testid="step-configure">
            <Input
              label="Agent Name"
              placeholder="e.g. Code Review Agent"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
            />
            <Select
              label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              error={errors.model}
            >
              {modelOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                System Prompt
              </label>
              <textarea
                data-testid="system-prompt-input"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful AI agent that..."
                rows={8}
                className="w-full resize-y rounded-md border border-white/10 bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Permissions
              </label>
              <div className="flex flex-wrap gap-2">
                {defaultPermissions.map((perm) => (
                  <button
                    key={perm}
                    type="button"
                    onClick={() => togglePermission(perm)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                      permissions[perm] === "allow"
                        ? "border-primary/50 bg-primary/10 text-primary"
                        : "border-white/10 text-muted-foreground hover:border-white/20"
                    }`}
                  >
                    {perm}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep("choose")}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={() => {
                  if (validate()) setStep("review");
                }}
              >
                Review
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === "review" && (
          <div className="max-w-2xl" data-testid="step-review">
            <div className="rounded-xl border border-white/10 bg-surface-raised p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">
                Review Agent Configuration
              </h2>
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-medium uppercase text-muted">Name</dt>
                  <dd className="mt-1 text-sm text-foreground">{name}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase text-muted">Model</dt>
                  <dd className="mt-1">
                    <Badge variant="primary">{model}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase text-muted">System Prompt</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {systemPrompt || "(none)"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase text-muted">Permissions</dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {Object.keys(permissions).length === 0 ? (
                      <span className="text-sm text-muted-foreground">(none)</span>
                    ) : (
                      Object.entries(permissions).map(([k, v]) => (
                        <Badge key={k} variant={v === "allow" ? "success" : "danger"}>
                          {k}
                        </Badge>
                      ))
                    )}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" onClick={() => setStep("configure")}>
                <ArrowLeft className="h-4 w-4" />
                Edit
              </Button>
              <Button onClick={handleCreate} loading={isLoading}>
                <Check className="h-4 w-4" />
                Create Agent
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
